import { Injectable, computed, inject, signal } from '@angular/core';
import { catchError, of } from 'rxjs';

import { ConsentApiService } from '../api/services';
import { CONSENT_TYPE, ConsentItemDto, RecordConsentDto } from '../api/models';

/** Persisted, host-scoped state of the visitor's cookie decision. */
export interface ConsentState {
  /** Whether the visitor has made (and saved) a choice yet. */
  decided: boolean;
  /** Analytics / statistics cookies (GA4, GTM, Meta Pixel). */
  analytics: boolean;
  /** Marketing cookies. */
  marketing: boolean;
}

/** Custom category selection persisted via {@link ConsentService.save}. */
export interface ConsentSelection {
  analytics: boolean;
  marketing: boolean;
}

const STORAGE_PREFIX = 'zaklepuj_consent::';
const STORAGE_VERSION = 1;

const DEFAULT_STATE: ConsentState = {
  decided: false,
  analytics: false,
  marketing: false,
};

/**
 * Zarządza stanem zgody na cookies dla hostów white-label (RODO/GDPR).
 *
 * Stan trzymany jest w localStorage pod kluczem zależnym od hosta, więc każdy
 * branded host pamięta własną decyzję. Niezbędne/funkcjonalne cookies są zawsze
 * włączone i nie podlegają wyłączeniu. Każdy zapis utrwala stan lokalnie ORAZ
 * wysyła PUT /consents (ścieżka anonimowa urządzenia) z kontekstem 'white_label'
 * i bieżącym `window.location.host`.
 */
@Injectable({ providedIn: 'root' })
export class ConsentService {
  private readonly api = inject(ConsentApiService);

  private readonly _state = signal<ConsentState>(this.loadFromStorage());

  readonly state = this._state.asReadonly();
  readonly decided = computed(() => this._state().decided);
  readonly analyticsGranted = computed(() => this._state().analytics);
  readonly marketingGranted = computed(() => this._state().marketing);

  /** Grant every optional category. */
  acceptAll(): void {
    this.commit({ decided: true, analytics: true, marketing: true });
  }

  /** Keep only the always-on essential/functional cookies. */
  rejectNonEssential(): void {
    this.commit({ decided: true, analytics: false, marketing: false });
  }

  /** Persist a custom per-category selection from the settings panel. */
  save(custom: ConsentSelection): void {
    this.commit({
      decided: true,
      analytics: custom.analytics,
      marketing: custom.marketing,
    });
  }

  /**
   * Re-open the banner/settings without discarding the recorded preferences.
   * Only flips `decided` so the UI shows again; categories stay as chosen.
   */
  reopen(): void {
    this._state.update((s) => ({ ...s, decided: false }));
  }

  // ── internals ────────────────────────────────────────────────────────────

  private commit(next: ConsentState): void {
    this._state.set(next);
    this.saveToStorage(next);
    this.record(next);
  }

  /**
   * Fire-and-forget record of the decision to the backend. Failures are
   * swallowed: the local decision still stands and analytics gating reads from
   * the signal, not the server.
   */
  private record(state: ConsentState): void {
    const host = typeof window !== 'undefined' ? window.location.host : '';
    if (!host) {
      return;
    }
    const consents: ConsentItemDto[] = [
      // Functional/necessary is always-on.
      { type: CONSENT_TYPE.FUNCTIONAL, granted: true },
      { type: CONSENT_TYPE.ANALYTICS, granted: state.analytics },
      { type: CONSENT_TYPE.MARKETING, granted: state.marketing },
    ];
    const payload: RecordConsentDto = { context: 'white_label', host, consents };

    this.api
      .recordConsent(payload)
      .pipe(catchError(() => of(void 0)))
      .subscribe();
  }

  private storageKey(): string {
    const host = typeof window !== 'undefined' ? window.location.host : 'unknown';
    return `${STORAGE_PREFIX}${host}::v${STORAGE_VERSION}`;
  }

  private loadFromStorage(): ConsentState {
    if (typeof localStorage === 'undefined') {
      return { ...DEFAULT_STATE };
    }
    try {
      const raw = localStorage.getItem(this.storageKey());
      if (!raw) {
        return { ...DEFAULT_STATE };
      }
      const parsed = JSON.parse(raw) as Partial<ConsentState>;
      return {
        decided: parsed.decided === true,
        analytics: parsed.analytics === true,
        marketing: parsed.marketing === true,
      };
    } catch {
      return { ...DEFAULT_STATE };
    }
  }

  private saveToStorage(state: ConsentState): void {
    if (typeof localStorage === 'undefined') {
      return;
    }
    try {
      localStorage.setItem(this.storageKey(), JSON.stringify(state));
    } catch {
      console.warn('[ConsentService] Failed to persist consent state');
    }
  }
}
